<template>
  <div class="custom-dropdown" :class="{ open: isOpen }" ref="dropdownRef">
    <div class="dropdown-trigger" @click="toggle">
      <span :class="{ placeholder: !selectedLabel }">{{ selectedLabel || placeholder }}</span>
      <div class="icons">
        <span v-if="selectedLabel && clearable" class="clear-icon" @click.stop="clearSelection">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        <svg class="arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
    </div>
    <transition name="dropdown-fade">
      <div v-if="isOpen" class="dropdown-menu">
        <div v-if="searchable" class="dropdown-search">
          <input 
            type="text" 
            v-model="searchQuery" 
            ref="searchInput"
            placeholder="Search..." 
            @click.stop
          />
        </div>
        <ul class="dropdown-options">
          <li 
            v-for="option in filteredOptions" 
            :key="option.value" 
            class="dropdown-option"
            :class="{ selected: modelValue === option.value }"
            @click="select(option)"
          >
            {{ option.label }}
          </li>
          <li v-if="filteredOptions.length === 0" class="no-results">
            No results found
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'CustomDropdown',
  props: {
    modelValue: [String, Number],
    options: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: "Select"
    },
    labelKey: {
      type: String,
      default: "name"
    },
    valueKey: {
      type: String,
      default: "name"
    },
    searchable: {
      type: Boolean,
      default: true
    },
    clearable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      isOpen: false,
      searchQuery: ""
    }
  },
  computed: {
    normalizedOptions() {
      // Normalize options to { label, value } format
      return this.options.map(opt => {
        if (typeof opt === 'object') {
          return {
            label: opt[this.labelKey],
            value: opt[this.valueKey],
            raw: opt
          }
        }
        return { label: opt, value: opt, raw: opt };
      });
    },
    selectedLabel() {
      if (!this.modelValue && this.modelValue !== 0) return "";
      const selected = this.normalizedOptions.find(opt => opt.value === this.modelValue);
      return selected ? selected.label : this.modelValue;
    },
    filteredOptions() {
      if (!this.searchQuery) return this.normalizedOptions;
      const query = this.searchQuery.toLowerCase();
      return this.normalizedOptions.filter(opt => 
        String(opt.label).toLowerCase().includes(query)
      );
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    toggle() {
      this.isOpen = !this.isOpen;
      if (this.isOpen && this.searchable) {
        this.searchQuery = "";
        this.$nextTick(() => {
          if (this.$refs.searchInput) {
            this.$refs.searchInput.focus();
          }
        });
      }
    },
    select(option) {
      this.$emit('update:modelValue', option.value);
      this.$emit('change', option.raw);
      this.isOpen = false;
    },
    clearSelection() {
      this.$emit('update:modelValue', "");
      this.$emit('change', null);
    },
    handleClickOutside(e) {
      if (this.$refs.dropdownRef && !this.$refs.dropdownRef.contains(e.target)) {
        this.isOpen = false;
      }
    }
  }
}
</script>

<style scoped>
.custom-dropdown {
  position: relative;
  width: 100%;
}

.dropdown-trigger {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s;
  min-height: 48px;
}

.custom-dropdown.open .dropdown-trigger,
.dropdown-trigger:hover {
  border-color: #198754;
}

.placeholder {
  color: #888;
}

.arrow {
  transition: transform 0.3s ease;
  color: #666;
}

.custom-dropdown.open .arrow {
  transform: rotate(180deg);
}

.icons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.clear-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  padding: 2px;
  border-radius: 50%;
  transition: all 0.2s;
}

.clear-icon:hover {
  background: #eee;
  color: #666;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background: white;
  border: 1px solid #eee;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  margin-top: 6px;
  z-index: 100;
  overflow: hidden;
}

.dropdown-search {
  padding: 8px;
  border-bottom: 1px solid #eee;
}

.dropdown-search input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  outline: none;
}

.dropdown-search input:focus {
  border-color: #198754;
}

.dropdown-options {
  max-height: 250px;
  overflow-y: auto;
  padding: 6px 0;
  list-style: none;
  margin: 0;
}

.dropdown-option {
  padding: 12px 16px;
  cursor: pointer;
  font-size: 14px;
  color: #333;
  transition: all 0.1s;
}

.dropdown-option:hover {
  background: #198754;
  color: white;
}

.dropdown-option.selected {
  background: rgba(25, 135, 84, 0.08);
  color: #198754;
  font-weight: 600;
}

.dropdown-option.selected:hover {
  background: #198754;
  color: white;
}

.no-results {
  padding: 12px 16px;
  color: #888;
  font-size: 14px;
  text-align: center;
}

/* Animation */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Scrollbar */
.dropdown-options::-webkit-scrollbar {
  width: 6px;
}
.dropdown-options::-webkit-scrollbar-track {
  background: #f1f1f1;
}
.dropdown-options::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 3px;
}
</style>
